import MaterialTable, { MTableAction } from '@material-table/core';
import { DeleteOutline, EditOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined, NumbersOutlined, SpeedOutlined, WarningAmberOutlined } from '@mui/icons-material';
import {
	Box,
	Card,
	CardContent,
	Collapse,
	IconButton,
	Tab,
	Tabs,
	Typography
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { enqueueSnackbar } from 'notistack';
import { phone } from 'phone';
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { CallerIdPerformance } from 'src/components/engagement/callerIdMonitoring';
import { PageAppBar } from 'src/components/pageAppBar';
import { tabStyle } from 'src/theme';
import { UserContext } from '../../contexts/UserContext';
import { createCallerId, deleteCallerId, updateCallerId } from '../../graphql/mutations';
import { callerIdsByTenant } from '../../graphql/queries';

export function CallerIds() {
	const client = generateClient();
	const userContext = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	const [callerIds, setCallerIds] = useState([]);
	const [newNumberTrigger, setNewNumberTrigger] = useState(false);
	const [currentTab, setCurrentTab] = useState(0);
	const [expanded, setExpanded] = useState(true);

	const numberTableRef = useRef();

	useEffect(() => {
		async function getNumbers() {
			setLoading(true);
			const savedCallerIds = await client.graphql({
				query: callerIdsByTenant,
				variables: { tenant: userContext.tenantId },
			});
			setCallerIds(
				savedCallerIds.data.callerIdsByTenant.items.sort(
					(a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
				),
			);
			setLoading(false);
		}
		if (userContext.tenantId) {
			getNumbers();
		}
	}, [userContext.tenantId]);

	function isNumberValid(number) {
		return phone(number, { validateMobilePrefix: false }).isValid;
	}

	function handleAddNewNumber() {
		numberTableRef.current.state.showAddRow = true;
		setNewNumberTrigger(!newNumberTrigger);
	}

	return (
		<Box>
			<PageAppBar
				title="Caller Id Numbers"
				description="Manage caller ID numbers for outbound calls."
				actionOneText={
					userContext.securityOptions.callerId > 1 ? '+ Number' : ''
				}
				actionOneText={
					userContext.securityOptions.callerId > 1 && currentTab === 0 ? '+ Number' : ''
				}
				actionOneHandler={() => {
					handleAddNewNumber()
				}}
				actionOneDisabled={userContext.securityOptions.callerId < 2}
			/>
			<Tabs
				value={currentTab}
				indicatorColor="primary"
				textColor="primary"
				onChange={(e, newValue) => setCurrentTab(newValue)}
			>
				<Tab sx={tabStyle} icon=<NumbersOutlined color={currentTab === 0 ? "primary" : "black"} /> label="Caller Ids" />
				<Tab sx={tabStyle} icon=<SpeedOutlined color={currentTab === 1 ? "primary" : "black"} /> label="Monitoring" />
			</Tabs>
			{currentTab === 0 &&
				<Box display="flex" flexDirection="column" gap={2}>
					<Card
						variant="outlined"
						sx={{
							borderRadius: 3,
							borderColor: "divider",
							overflow: "hidden",
						}}
					>
						<Box
							sx={{
								px: 3,
								py: 2,
								borderBottom: expanded ? "1px solid" : "none",
								borderColor: "divider",
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 2,
							}}
						>
							<Box>
								<Typography
									variant="overline"
									sx={{
										color: "warning.main",
										fontWeight: 700,
										letterSpacing: 1,
									}}
								>
									Compliance Notice
								</Typography>

								<Typography
									variant="h6"
									sx={{
										fontWeight: 700,
										lineHeight: 1.2,
									}}
								>
									Cross-Border & International Communications
								</Typography>
							</Box>

							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								<Box
									sx={{
										width: 40,
										height: 40,
										borderRadius: 2,
										bgcolor: "warning.light",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
									}}
								>
									<WarningAmberOutlined
										sx={{
											color: "warning.dark",
											fontSize: 22,
										}}
									/>
								</Box>

								<IconButton
									onClick={() => setExpanded(!expanded)}
									size="small"
									sx={{
										border: "1px solid",
										borderColor: "divider",
										bgcolor: "background.paper",
									}}
								>
									{expanded ? (
										<KeyboardArrowUpOutlined />
									) : (
										<KeyboardArrowDownOutlined />
									)}
								</IconButton>
							</Box>
						</Box>

						<Collapse in={expanded}>
							<CardContent sx={{ p: 3 }}>
								<Typography
									variant="body2"
									sx={{
										color: "text.secondary",
										lineHeight: 1.8,
										fontSize: "0.95rem",
									}}
								>
									Company shall be solely responsible for identifying, satisfying, and maintaining compliance with all laws, rules, regulations, regulatory guidance, industry standards, carrier requirements, and telecommunications provider requirements applicable to its outbound communications in each jurisdiction in which such communications are originated, routed, transmitted, received, or otherwise conducted. Without limiting the foregoing, Company shall ensure that its outbound communications comply with all applicable jurisdiction-specific requirements relating to caller identification, number presentation, number ownership or authorization, anti-spoofing, consent, registration, licensing, calling hours, do-not-call or suppression lists, opt-out rights, call recording or monitoring disclosures, data protection and privacy, cross-border data transfers, and cross-border communications. Company shall not use Customer Dynamics’ services to place, route, originate, transmit, or facilitate any outbound communication unless Company has obtained and maintains all rights, permissions, registrations, approvals, consents, and authorizations required for such communication in all applicable jurisdictions.
								</Typography>
							</CardContent>
						</Collapse>
					</Card>

					<MaterialTable
						tableRef={numberTableRef}
						title=""
						isLoading={loading}
						columns={[
							{ title: 'Number', field: 'number', validate: rowData => isNumberValid(rowData.number) },
							{ title: 'Name (optional)', field: 'name' },
							{ title: 'Carrier (optional)', field: 'carrier' }
						]}
						data={callerIds}
						options={{
							actionsColumnIndex: -1,
							pageSize: 10,
							padding: 'dense',
							addRowPosition: 'first',
							searchFieldStyle: {
								marginBottom: '16px',
								marginLeft: '-28px',
							},
							headerStyle: {
								borderTop: '1px solid #e0e0e0',
								padding: '16px',
							},
							searchAutoFocus: true,
							searchFieldVariant: 'outlined',
							paginationType: 'stepped',
						}}
						components={{
							Action: props => {
								if (props.action.tooltip !== "Add") return <MTableAction {...props} />;
								else return null;
							},
							Container: (props) => <Box {...props} elevation={0} />,
						}}
						icons={{
							Edit: forwardRef((props, ref) => <EditOutlined {...props} ref={ref} color='primary' />),
							Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} color='primary' />),
						}}
						editable={{
							// isEditable: selDncList === null,
							onRowAdd: newData => new Promise(async (resolve, reject) => {
								try {
									// Check number isn't already in list before attempting to add
									newData.number = phone(newData.number, { validateMobilePrefix: false }).phoneNumber;
									const existingNumbers = callerIds.map(c => c.number);
									if (existingNumbers.includes(newData.number)) {
										enqueueSnackbar("Number already exists in list", { variant: 'error' });
										reject();
										return;
									}

									setCallerIds([...callerIds, { ...newData, tenant: userContext.tenantId }]);
									await client.graphql({
										query: createCallerId,
										variables: {
											input: {
												number: newData.number,
												name: newData.name,
												carrier: newData.carrier,
												tenant: userContext.tenantId,
											}
										}
									});
									enqueueSnackbar("Number added", { variant: 'success' });

									resolve();
								} catch (err) {
									console.log(err);
									reject();
								}
							}),
							onRowDelete: toDelete => new Promise(async (resolve, reject) => {
								try {
									await client.graphql({
										query: deleteCallerId,
										variables: {
											input: {
												id: toDelete.id
											}
										}
									});

									enqueueSnackbar("Number deleted", { variant: 'success' });
									callerIds.splice(toDelete.id, 1);
									setCallerIds([...callerIds]);
									resolve();
								} catch (err) {
									console.log(err);
									enqueueSnackbar("Unable to delete number", { variant: 'error' });
									reject();
								}
							}),
							onRowUpdate: (newData, oldData) => new Promise(async (resolve, reject) => {
								try {
									await client.graphql({
										query: updateCallerId,
										variables: {
											input: {
												id: newData.id,
												number: newData.number,
												name: newData.name,
												carrier: newData.carrier,
											}
										}
									});

									const dataUpdate = [...callerIds];
									const index = oldData.tableData.index;
									dataUpdate[index] = newData;
									setCallerIds([...dataUpdate]);
									1
									enqueueSnackbar("Number updated", { variant: 'success' });
									resolve();
								} catch (err) {
									console.log(err);
									enqueueSnackbar("Unable to update number", { variant: 'error' });
									reject();
								}
							})
						}}
					/>

				</Box>}
			{currentTab === 1 &&
				<CallerIdPerformance
					callerIds={callerIds}
					userContext={userContext}
				/>

			}
		</Box>
	);
}
